import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Upload, FileText, ArrowRight, ArrowLeft, CheckCircle, X, Lightbulb, Check } from "lucide-react";
import axios from "axios";
import { API } from "../../App";
import { CLINIC_COLORS, BTN_GRADIENT } from "./theme";

// Matches mockup m5:
// - max-w-4xl container, 2-column bento (main col-span-2, sidebar col-span-1)
// - Main: white card, description header w/ icon, filled textarea with focus ring, encrypted-data helper
// - Sidebar: guidance card (light primary tint), image accent, help card
// - Footer: ghost Back (left), gradient pill "Järgmine" (right)

const CARD_STYLE = {
  background: "#ffffff",
  borderRadius: "0.75rem",
  boxShadow: "0 4px 20px rgba(0,101,141,0.06)",
  border: "1px solid rgba(189,200,209,0.15)",
};

const FILLED_INPUT_STYLE = {
  background: "#f1f4f9",
  border: "2px solid transparent",
  transition: "border-color 0.2s, background-color 0.2s",
};

export function UltrasoundStep({
  ultrasoundText,
  onTextChange,
  onContinue,
  onSkip,
  onBack,
  token,
  isDoctorView = false,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractedNotice, setExtractedNotice] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setExtractedNotice(false);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/upload/ultrasound`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setUploadedFile({ name: file.name, id: response.data.file_id });
      if (response.data.extracted_text) {
        onTextChange(response.data.extracted_text);
        setExtractedNotice(true);
      }
    } catch (err) {
      console.error("Upload failed:", err);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clearFile = () => {
    setUploadedFile(null);
    setExtractedNotice(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in">
      {/* Main input area */}
      <div className="lg:col-span-2 space-y-6">
        <div className="p-8" style={CARD_STYLE} data-testid="ultrasound-card">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-6 h-6" style={{ color: CLINIC_COLORS.primary }} />
            <h2 className="text-lg font-semibold" style={{ color: "#181c20" }}>
              {t("dashboard.ultrasoundTextLabel") || "Sisestage raporti tekst"}
            </h2>
          </div>

          <textarea
            id="ultrasound-text"
            data-testid="input-ultrasound-text"
            className="w-full min-h-[360px] p-6 rounded-xl text-base leading-relaxed resize-none focus:outline-none"
            style={FILLED_INPUT_STYLE}
            onFocus={(e) => {
              e.target.style.borderColor = CLINIC_COLORS.primary;
              e.target.style.background = "#ffffff";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "transparent";
              e.target.style.background = "#f1f4f9";
            }}
            placeholder={t("dashboard.ultrasoundTextPlaceholder") || "Kleepige oma ultraheli vastuse tekst siia..."}
            value={ultrasoundText}
            onChange={(e) => onTextChange(e.target.value)}
          />

          {extractedNotice && (
            <p className="mt-3 text-sm flex items-center gap-1.5" style={{ color: CLINIC_COLORS.primary }}>
              <CheckCircle className="w-4 h-4" />
              {t("dashboard.ultrasoundExtracted")}
            </p>
          )}
        </div>

        {/* File upload card (below main textarea) */}
        <div className="p-6" style={CARD_STYLE}>
          <label className="font-medium text-sm block mb-3" style={{ color: CLINIC_COLORS.muted }}>
            {t("dashboard.ultrasoundUploadLabel")}
          </label>

          {uploadedFile ? (
            <div
              className="flex items-center gap-3 p-4 rounded-xl"
              style={{ background: "rgba(198,231,255,0.3)", border: "1px solid rgba(0,101,141,0.15)" }}
            >
              <FileText className="w-5 h-5 flex-shrink-0" style={{ color: CLINIC_COLORS.primary }} />
              <span className="text-sm flex-1 truncate" style={{ color: CLINIC_COLORS.primary }}>
                {uploadedFile.name}
              </span>
              <button
                onClick={clearFile}
                className="transition-opacity hover:opacity-70"
                style={{ color: CLINIC_COLORS.subtle }}
                data-testid="clear-uploaded-file"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full p-6 rounded-xl transition-all flex flex-col items-center gap-2 cursor-pointer disabled:opacity-50"
              style={{ background: "#f1f4f9", border: "2px dashed #bdc8d1" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = CLINIC_COLORS.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#bdc8d1";
              }}
              data-testid="upload-ultrasound-btn"
            >
              {uploading ? (
                <div className="flex items-center gap-2" style={{ color: CLINIC_COLORS.subtle }}>
                  <div
                    className="animate-spin w-5 h-5 border-2 rounded-full"
                    style={{ borderColor: CLINIC_COLORS.primary, borderTopColor: "transparent" }}
                  />
                  <span className="text-sm">{t("dashboard.ultrasoundUploading")}</span>
                </div>
              ) : (
                <>
                  <Upload className="w-6 h-6" style={{ color: CLINIC_COLORS.subtle }} />
                  <span className="text-sm" style={{ color: CLINIC_COLORS.subtle }}>
                    {t("dashboard.ultrasoundUploadHint")}
                  </span>
                </>
              )}
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            onChange={handleFileUpload}
            className="hidden"
            data-testid="ultrasound-file-input"
          />
        </div>
      </div>

      {/* Info sidebar */}
      <aside className="space-y-6">
        {/* Guidance card */}
        <div
          className="p-6 rounded-xl"
          style={{ background: "rgba(198,231,255,0.25)", border: "1px solid rgba(0,101,141,0.12)" }}
        >
          <div className="flex items-center gap-3 mb-3" style={{ color: CLINIC_COLORS.primary }}>
            <Lightbulb className="w-5 h-5" />
            <h3 className="font-bold">{t("dashboard.ultrasoundGuidanceTitle") || "Kuidas see toimib?"}</h3>
          </div>
          <p className="text-sm leading-relaxed mb-4" style={{ color: CLINIC_COLORS.muted }}>
            {t("dashboard.ultrasoundGuidanceBody") ||
              "Meie süsteem analüüsib teie ultraheli raportit, et tuvastada kilpnäärme suurust, sõlmede olemasolu ja teisi olulisi markereid."}
          </p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-xs" style={{ color: CLINIC_COLORS.muted }}>
              <Check className="w-4 h-4 shrink-0" style={{ color: CLINIC_COLORS.primary }} />
              {t("dashboard.ultrasoundTip1") || "Kopeerige kogu tekst digiloost."}
            </li>
            <li className="flex items-start gap-2 text-xs" style={{ color: CLINIC_COLORS.muted }}>
              <Check className="w-4 h-4 shrink-0" style={{ color: CLINIC_COLORS.primary }} />
              {t("dashboard.ultrasoundTip2") || "Veenduge, et kaasatud on mõõtmed."}
            </li>
          </ul>
        </div>

        {/* Help card */}
        <div className="p-6 rounded-xl" style={CARD_STYLE}>
          <h3 className="font-bold mb-2" style={{ color: "#181c20" }}>
            {t("dashboard.needHelp") || "Kas vajate abi?"}
          </h3>
          <p className="text-sm mb-3" style={{ color: CLINIC_COLORS.muted }}>
            {t("dashboard.needHelpText") || "Kui te ei leia vastust, võtke ühendust meie toega."}
          </p>
          <a
            href="mailto:support@kilpnäärmekliinik.ee"
            className="font-semibold text-sm flex items-center gap-1 hover:underline"
            style={{ color: CLINIC_COLORS.primary }}
            data-testid="ultrasound-help-link"
          >
            {t("dashboard.openSupport") || "Ava klienditugi"}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </aside>

      {/* Nav footer - spans full width */}
      <div className="lg:col-span-3 mt-4 flex flex-col md:flex-row items-center justify-between gap-4">
        <button
          type="button"
          data-testid="ultrasound-back-btn"
          onClick={onBack}
          className="order-3 md:order-1 px-8 py-3 font-semibold rounded-full transition-all inline-flex items-center gap-2"
          style={{ color: CLINIC_COLORS.primary, background: "transparent" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#ebeef3")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back") || t("dashboard.backToLabs") || "Tagasi"}
        </button>

        <div className="order-1 md:order-2 flex flex-col md:flex-row gap-3 w-full md:w-auto md:items-center">
          <button
            type="button"
            data-testid="skip-ultrasound-btn"
            onClick={onSkip}
            className="px-8 py-3 font-semibold rounded-full transition-all"
            style={{ color: CLINIC_COLORS.muted, background: "transparent" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#ebeef3")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {t("dashboard.ultrasoundSkip")}
          </button>
          <button
            type="button"
            data-testid="continue-ultrasound-btn"
            onClick={onContinue}
            disabled={!ultrasoundText?.trim()}
            className="w-full md:w-auto px-12 py-4 text-white font-bold rounded-full shadow-lg transition-all hover:scale-[1.02] active:scale-95 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{ background: BTN_GRADIENT, boxShadow: "0 8px 20px rgba(0,101,141,0.25)" }}
          >
            {t("dashboard.ultrasoundContinue") || "Järgmine"}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
